import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { CampaignAnalyticsService } from '@core/services/campaign-analytics.service';
import { TSocialContent } from '@interfaces/campaign-analytics-api.interface';
import { ComponentStore } from '@ngrx/component-store';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { EMPTY, MonoTypeOperatorFunction, Observable, pipe } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { ICreator } from '../creators.interface';
import { CreatorsService } from '../creators.service';

interface ICreatorDetailsState {
  creator: ICreator | null;
  creatorLoading: boolean;
  socialContents: TSocialContent[] | null;
  loading: boolean;
  error: string | null;
}

const initialState: ICreatorDetailsState = {
  creator: null,
  creatorLoading: false,
  error: null,
  loading: false,
  socialContents: null,
};

@Injectable()
export class CreatorDetailsStore extends ComponentStore<ICreatorDetailsState> {
  public readonly creator$ = this.selectSignal((state: ICreatorDetailsState) => state.creator);
  public readonly creatorLoading$ = this.selectSignal((state: ICreatorDetailsState) => state.creatorLoading);
  public readonly socialContents$ = this.selectSignal((state: ICreatorDetailsState) => state.socialContents);
  public readonly loading$ = this.selectSignal((state: ICreatorDetailsState) => state.loading);
  public readonly error$ = this.selectSignal((state: ICreatorDetailsState) => state.error);

  private readonly handleError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError(() => {
        this.patchState({
          error: errorMessage,
          loading: false,
        });

        return EMPTY;
      }),
    );

  public readonly fetchCreator = this.effect(
    (
      params$: Observable<{
        creatorId: string;
        adminMode?: boolean;
      }>,
    ) => {
      return params$.pipe(
        tap(() => this.patchState({ creatorLoading: true, error: null })),
        switchMap(({ creatorId, adminMode }) => {
          const httpParams = new HttpParams().set('joins', 'channels,campaign_creators,user_invitation');

          const fetch$ = adminMode
            ? this.creatorsService.getAdminCreator(creatorId, httpParams)
            : this.creatorsService.getCreator(creatorId, httpParams);

          return fetch$.pipe(
            tap((creator: ICreator | undefined) => {
              this.patchState({ creator: creator ?? null, creatorLoading: false });
            }),
            catchError(() => {
              this.patchState({ creatorLoading: false, error: 'Failed to load creator' });
              this.router.navigate([adminMode ? '/admin/creators' : '/creators']);

              return EMPTY;
            }),
          );
        }),
      );
    },
  );

  public readonly fetchSocialContents = this.effect(
    (trigger$: Observable<{ creatorId: string; channelTechnicalIds?: string[]; startDate?: Date; endDate?: Date }>) => {
      return trigger$.pipe(
        tap(() => this.patchState({ error: null, loading: true })),
        switchMap(({ creatorId, startDate, endDate }) => {
          let httpParams = new HttpParams().set('limit', '1000').set('order_by', '-execution_time');

          if (startDate) {
            httpParams = httpParams.set('execution_time__gte', this.dateUtils.formatToApiDateTime(startDate)!);
          }

          if (endDate) {
            httpParams = httpParams.set('execution_time__lte', this.dateUtils.formatToApiDateTime(endDate)!);
          }

          return this.analyticsService.getCreatorSocialContents(creatorId, httpParams).pipe(
            tap((socialContents: IApiResponse<TSocialContent[]>) => {
              this.patchState({ loading: false, socialContents: socialContents?.data ?? [] });
            }),
            this.handleError('Failed to load social contents'),
          );
        }),
      );
    },
  );

  constructor(
    private readonly analyticsService: CampaignAnalyticsService,
    private readonly creatorsService: CreatorsService,
    private readonly dateUtils: DateUtilitiesService,
    private readonly router: Router,
  ) {
    super(initialState);
  }
}
